import { supabase } from '../supabase'
import { INGREDIENT_CARDS } from '../../games/ideatthat/data/ingredientCards'
import type {
  WeeklyEventRow, WeeklySubmissionRow, WeeklyVoteRow, WeeklyWinnerRow,
} from '../database.types'

/**
 * Only real ingredients (not preparation methods) are eligible for the
 * weekly "shared + random" pick — the weekly event is about making a
 * meal from a specific set of ingredients, not a specific set of verbs.
 */
const INGREDIENT_POOL = INGREDIENT_CARDS
  .filter(c => c.category !== 'method')
  .map(c => c.id)

export interface WeeklyPeriods {
  /** The event you write stories for (current week). */
  write: WeeklyEventRow
  /** The event you vote on (last week's submissions, already locked). */
  vote:  WeeklyEventRow | null
}

/** ISO Monday (UTC) of the date's week, as YYYY-MM-DD. */
export function mondayOf(date: Date): string {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
  const day = d.getUTCDay() || 7       // Sunday = 7
  if (day !== 1) d.setUTCDate(d.getUTCDate() - (day - 1))
  return d.toISOString().slice(0, 10)
}

/** Current Monday + previous Monday in YYYY-MM-DD. */
export function currentWeekStarts(now = new Date()): { thisWeek: string; lastWeek: string } {
  const thisWeek = mondayOf(now)
  const d = new Date(thisWeek)
  d.setUTCDate(d.getUTCDate() - 7)
  return { thisWeek, lastWeek: d.toISOString().slice(0, 10) }
}

export interface WeeklyCardOption {
  game: 'iet' | 'colorful' | 'three_step'
  entered: boolean
  week_start: string
  subjects: string[] | null
  action_a: string | null
  action_b: string | null
}

/** This week's three weeklies with per-user entered flags, for the
 *  hub's single randomly-chosen card. Guests read entered=false. */
export async function fetchWeeklyCardOptions(): Promise<WeeklyCardOption[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any).rpc('weekly_card_options')
  if (error) throw error
  return Array.isArray(data) ? (data as WeeklyCardOption[]) : []
}

export const UNTITLED_DISH = 'Untitled dish'

/**
 * How a dish is presented in lists, cards, and leaderboards. Preference:
 * the user's chosen title, then the author's display name (so legacy
 * entries without a title still read as "<player>"), then a constant.
 */
export function dishDisplayName(
  sub: { title?: string | null } | null | undefined,
  author: { display_name?: string | null } | null | undefined,
): string {
  const title = sub?.title?.trim()
  if (title) return title
  const name = author?.display_name?.trim()
  if (name) return name
  return UNTITLED_DISH
}

/**
 * Human-readable week label, e.g. "Apr 20 – Apr 26". Used by live /
 * voting / hub screens where the precise window is meaningful.
 *
 * Weeks run Mon–Sun in US Eastern time (the canonical event window
 * — see `endOfWeekET`). We render the dates in UTC because the
 * stored week_start is already a UTC midnight Monday and `+6 days`
 * lands on the matching Sunday; falling back to the browser's local
 * time would shift PST users one day earlier.
 */
/** "Week of Jun 29 – Jul 5" — which week a submission lands in. UTC on
 *  purpose: weeks flip at midnight UTC Sunday→Monday, and showing the range
 *  keeps a Monday-morning session from being mistaken for last week. */
export function weekRangeLabel(weekStart: string): string {
  const start = new Date(weekStart + 'T00:00:00Z')
  const end = new Date(start); end.setUTCDate(end.getUTCDate() + 6)
  const fmt = (d: Date) => d.toLocaleDateString('en-US', {
    timeZone: 'UTC', month: 'short', day: 'numeric',
  })
  return `Week of ${fmt(start)} – ${fmt(end)}`
}

export function weekLabel(weekStart: string): string {
  const fmt: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' }
  const start = new Date(weekStart + 'T00:00:00Z')
  const end = new Date(start); end.setUTCDate(end.getUTCDate() + 6)
  return `${start.toLocaleDateString('en-US', fmt)} – ${end.toLocaleDateString('en-US', fmt)}`
}

/**
 * Archive label — the week's final (Sunday) date with the year, e.g.
 * "APR 26 2026". Used in the archive lists where the precise window
 * matters less than just identifying which week finalised. UTC for
 * the same reason `weekLabel` uses UTC.
 */
export function archiveDateLabel(weekStart: string): string {
  const start = new Date(weekStart + 'T00:00:00Z')
  const end = new Date(start); end.setUTCDate(end.getUTCDate() + 6)
  return end.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  }).replace(',', '').toUpperCase()
}

/**
 * The exact instant a writing week ends: end of Sunday at midnight ET
 * (= the following Monday 00:00 in America/New_York). Uses Intl to get
 * the offset for the target date so DST flips (Mar / Nov) handle
 * themselves — no hard-coded ±4/±5h.
 */
export function endOfWeekET(weekStart: string): Date {
  const start = new Date(weekStart + 'T00:00:00Z')
  // Build the actual target Date and read its normalised day-of-month.
  // Previous bug: compared `nyDay` (1-31) against an un-normalised
  // `start.getUTCDate() + 7` (which could be 32-37 near month end), so
  // the day-match check could never succeed and the fix-up loop
  // wrongly pushed `guess` forward — adding spurious days to the
  // countdown.
  const target = new Date(Date.UTC(
    start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate() + 7,
  ))
  const targetD = target.getUTCDate()
  let guess = target.getTime()
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit',
  })
  for (let attempt = 0; attempt < 3; attempt++) {
    const parts = fmt.formatToParts(new Date(guess))
    const get = (t: string) => parseInt(parts.find(p => p.type === t)?.value ?? '0')
    const nyDay = get('day')
    let nyHour = get('hour'); if (nyHour === 24) nyHour = 0
    if (nyDay === targetD && nyHour === 0) return new Date(guess)
    if (nyDay !== targetD) {
      // Still on the previous calendar day in NY — push forward.
      guess += (24 - nyHour) * 3_600_000
    } else {
      // Past midnight on the right day — pull back.
      guess -= nyHour * 3_600_000
    }
  }
  return new Date(guess)
}

/**
 * Days remaining until end of Sunday midnight ET. Always rounds up so
 * "1d left" reads correctly through the final partial day.
 */
export function daysLeftInWeek(weekStart: string, now: Date = new Date()): number {
  const ms = endOfWeekET(weekStart).getTime() - now.getTime()
  return Math.max(0, Math.ceil(ms / 86_400_000))
}

/**
 * Ensure the current week's event exists (creates it on first call of
 * the week). Then fetch the previous week's event (for voting). Also
 * finalizes last week's submissions as a side-effect of creation.
 */
export async function getWeeklyPeriods(): Promise<WeeklyPeriods> {
  const { thisWeek, lastWeek } = currentWeekStarts()

  const write = await ensureEvent(thisWeek)
  const vote  = await fetchEvent(lastWeek)

  return { write, vote }
}

async function ensureEvent(weekStart: string): Promise<WeeklyEventRow> {
  const { data, error } = await supabase.rpc('get_or_create_weekly_event', {
    p_week_start:      weekStart,
    p_ingredient_pool: INGREDIENT_POOL,
  })
  if (error) throw error
  return data as WeeklyEventRow
}

async function fetchEvent(weekStart: string): Promise<WeeklyEventRow | null> {
  const { data, error } = await supabase
    .from('weekly_events')
    .select('*')
    .eq('week_start', weekStart)
    .maybeSingle()
  if (error) throw error
  return data as WeeklyEventRow | null
}

/**
 * Read-only: my submission row for an event, or null. The hub uses
 * this so LOOKING at the weekly never creates a row; the row is born
 * in the editor, where writing actually starts.
 */
export async function fetchMySubmissionRow(
  eventId: string,
  profileId: string,
): Promise<WeeklySubmissionRow | null> {
  const { data, error } = await supabase
    .from('weekly_submissions')
    .select('*')
    .eq('event_id', eventId)
    .eq('profile_id', profileId)
    .maybeSingle()
  if (error) throw error
  return (data ?? null) as WeeklySubmissionRow | null
}

/** Upsert my submission for an event, deriving my 2 random ingredients. */
export async function ensureMySubmission(eventId: string): Promise<WeeklySubmissionRow> {
  const { data, error } = await supabase.rpc('ensure_weekly_submission', {
    p_event_id:        eventId,
    p_ingredient_pool: INGREDIENT_POOL,
  })
  if (error) throw error
  return data as WeeklySubmissionRow
}

export async function saveMySubmission(
  submissionId: string,
  title: string,
  story: string,
): Promise<WeeklySubmissionRow> {
  const { data, error } = await supabase.rpc('save_weekly_submission', {
    p_submission_id: submissionId,
    p_title: title,
    p_story: story,
  })
  if (error) throw error
  return data as WeeklySubmissionRow
}

/** Delete my (unlocked) submission entirely — the editor's X-out. */
export async function withdrawMySubmission(submissionId: string): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase as any).rpc('withdraw_weekly_submission', {
    p_submission_id: submissionId,
  })
  if (error) throw error
}

/**
 * Past winners backlog — one row per finalised week (newest first).
 * Pass the current "vote" week's start to exclude it from the list,
 * so the hub doesn't duplicate the week it already shows as a card.
 */
export async function fetchWeeklyWinners(
  excludeWeekStart?: string | null,
): Promise<WeeklyWinnerRow[]> {
  const { data, error } = await supabase.rpc('list_weekly_winners', {
    p_exclude_week_start: excludeWeekStart ?? null,
    // Five years of weeks, i.e. effectively uncapped. Trophies have no
    // cap, so any limit low enough to bite would start hiding weeks
    // that still hand out trophies. The server default is 20.
    p_limit: 260,
  })
  if (error) throw error
  return (data ?? []) as WeeklyWinnerRow[]
}

/** All submissions for an event, already-locked ones first. */
export async function fetchGallery(eventId: string): Promise<WeeklySubmissionRow[]> {
  const { data, error } = await supabase
    .from('weekly_submissions')
    .select('*')
    .eq('event_id', eventId)
    .order('total_votes', { ascending: false })
  if (error) throw error
  return (data ?? []) as WeeklySubmissionRow[]
}

/** The profiles for a list of submissions, keyed by id. */
export async function fetchSubmissionAuthors(profileIds: string[]): Promise<Record<string, { display_name: string; avatar_url: string | null }>> {
  if (profileIds.length === 0) return {}
  const { data, error } = await supabase
    .from('profiles')
    .select('id, display_name, avatar_url')
    .in('id', profileIds)
  if (error) throw error
  const map: Record<string, { display_name: string; avatar_url: string | null }> = {}
  for (const p of data ?? []) map[p.id] = { display_name: p.display_name, avatar_url: p.avatar_url }
  return map
}

/** Toggle my like on a submission. Returns the new total_votes count. */
export async function toggleWeeklyVote(submissionId: string): Promise<number> {
  const { data, error } = await supabase.rpc('cast_weekly_vote', {
    p_submission_id: submissionId,
  })
  if (error) throw error
  return data as number
}

/** Which submissions (of these) have I already voted on? */
export async function fetchMyVotedSubmissionIds(
  submissionIds: string[],
  voterId: string,
): Promise<Set<string>> {
  if (submissionIds.length === 0) return new Set()
  const { data, error } = await supabase
    .from('weekly_votes')
    .select('submission_id')
    .in('submission_id', submissionIds)
    .eq('voter_id', voterId)
  if (error) throw error
  return new Set((data ?? []).map((v: Pick<WeeklyVoteRow, 'submission_id'>) => v.submission_id))
}
